import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  authedFetch,
  hasStoredSession,
  SessionExpiredError,
  useStoredAuth,
  type StoredAuth,
} from "../../../utils/auth";
import { resolveApiDomain } from "../../../utils/app-domains";

/**
 * Shape of `GET {baseUrl}/notifications` rows, live-verified against
 * `https://api.dev.nazare.app/notifications?limit=20` (nazare-frontend's
 * real notifications endpoint — see `app/web/app/hooks/use-notifications.ts`
 * and `app/web/app/features/sse/sse-topics.ts`'s `notificationsTopic()` for
 * where this same base URL/path is used there).
 *
 * Note `created_at`/`read_at`/`dismissed_at` are epoch-millisecond numbers
 * on the wire, NOT ISO strings — nazare-frontend's own `Notification` type
 * declares them as `string`, but the live payload disagrees (confirmed by
 * fetching the endpoint directly). `new Date(x)` accepts either shape, so
 * this is only a type-accuracy fix here, not a behavior change.
 */
export type Notification = {
  id: string;
  notification_type: string;
  reference_id: string | null;
  title: string;
  body: string | null;
  url: string | null;
  status: string;
  read_at: number | null;
  dismissed_at: number | null;
  created_at: number;
};

// This app has no `getEnv()`/`getServiceDomain()` wrapper (that lives in
// nazare-frontend's app/web only) and packages/ui's tsconfig sets `types: []`
// so `vite/client`'s `ImportMetaEnv` augmentation isn't ambiently available
// here — read `import.meta.env` through a cast rather than adding that
// ambient dependency to the whole package for one component. At runtime
// (bundled by whichever app consumes this component, e.g. `platform`) this
// is the exact same object Vite's `import.meta.env` always is.
function getApiBaseUrl(): string {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> })
    .env;
  return resolveApiDomain(window.location.origin, env?.VITE_NAZARE_API_DOMAIN);
}

async function fetchNotifications(
  baseUrl: string,
  auth: StoredAuth | null,
): Promise<Notification[]> {
  const res = await authedFetch(baseUrl, auth, "/notifications?limit=20");
  if (!res.ok) {
    if (res.status === 401) throw new SessionExpiredError();
    throw new Error(`Failed to fetch notifications: ${res.status}`);
  }
  const data: { notifications: Notification[] } = await res.json();
  return data.notifications;
}

const NOTIFICATIONS_QUERY_KEY = ["app-shell", "notifications"] as const;

/**
 * Live notification feed for `<NotificationBell>`. Polls on an interval
 * rather than subscribing to nazare-frontend's SSE stream
 * (`{baseUrl}/notifications/stream`, run through a cross-tab `SharedWorker`
 * in `app/web/app/features/sse/`) — reproducing that worker/reconnect/replay
 * machinery is out of scope for a single app-shell component, and polling a
 * `useQuery` is what this repo's own `createQueryClient()` defaults (see
 * `utils/shell-state/query-client.ts`) are tuned for.
 *
 * Disabled (no fetch attempted) when there's no stored auth session at all
 * — this app has no login flow of its own, only `useStoredAuth()`'s read of
 * whatever nazare-frontend session already sits in `localStorage`. This app
 * has no refresh path either: a missing or server-rejected access token
 * surfaces as `SessionExpiredError` (see `authedFetch`), which callers
 * render as a "sign in again" state rather than this feed retrying itself.
 */
export function useNotificationFeed() {
  const auth = useStoredAuth();
  const baseUrl = getApiBaseUrl();

  const {
    data: notifications = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: () => fetchNotifications(baseUrl, auth),
    enabled: hasStoredSession(auth),
    refetchInterval: 60_000,
  });

  const unreadCount = notifications.reduce(
    (count, n) => (n.read_at == null ? count + 1 : count),
    0,
  );

  return {
    notifications,
    unreadCount,
    hasUnread: unreadCount > 0,
    isLoading,
    isError,
    // Distinguishes "your session is gone, sign in again" from a generic
    // fetch/server error — see `SessionExpiredError`.
    isSessionExpired: error instanceof SessionExpiredError,
  };
}

/** Marks a single notification read; optimistic, rolls back on error. */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const auth = useStoredAuth();
  const baseUrl = getApiBaseUrl();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!hasStoredSession(auth)) return;
      const res = await authedFetch(baseUrl, auth, `/notifications/${id}/read`, {
        method: "PATCH",
      });
      if (!res.ok) {
        throw new Error(`Failed to mark notification read: ${res.status}`);
      }
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      const previous = queryClient.getQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY);
      queryClient.setQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY, (old) =>
        (old ?? []).map((n) => (n.id === id ? { ...n, read_at: n.read_at ?? Date.now() } : n)),
      );
      return { previous };
    },
    onError: (error, _id, context) => {
      console.error("[notification-bell]", error);
      if (context?.previous) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previous);
      }
    },
  });
}

/** Marks every notification read; optimistic, rolls back on error. */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const auth = useStoredAuth();
  const baseUrl = getApiBaseUrl();

  return useMutation({
    mutationFn: async () => {
      if (!hasStoredSession(auth)) return;
      const res = await authedFetch(baseUrl, auth, "/notifications/read-all", {
        method: "POST",
      });
      if (!res.ok) {
        throw new Error(`Failed to mark all notifications read: ${res.status}`);
      }
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      const previous = queryClient.getQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY);
      queryClient.setQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY, (old) =>
        (old ?? []).map((n) => ({ ...n, read_at: n.read_at ?? Date.now() })),
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      console.error("[notification-bell]", error);
      if (context?.previous) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previous);
      }
    },
  });
}
